import * as Sentry from '@sentry/browser';
import firebase from 'firebase/app';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { compose } from 'recompose';
import format from 'string-template';

import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useLogger from '../../../../hooks/useLogger';
import useSessionStore from '../../../../hooks/useSessionStore';
import Coach from '../../../../Model/Coach';
import ExternalCoach from '../../../../Model/ExternalCoach';
import Subscription from '../../../../Model/Subscription';
import UserContract from '../../../../Model/UserContract';
import { StripeSubscriptionState } from '../../../../pages/Onboarding/Subscription/SubscriptionMetadata';
import { isFutureDay } from '../../../../utils/date';
import { isDev, isTestRun } from '../../../../utils/environment';
import { MONEY_DECIMALS, formatAmoutInCents } from '../../../../utils/formatters';
import { CoachingActivity } from '../../../../utils/log';
import useToast from '../../../hooks/useToast';
import ChallengeConfig from '../../../Model/ChallengeConfig';
import LeadConfig from '../../../Model/LeadConfig';
import { CHALLENGE_CATEGORY, isChallengeStillAccepting } from '../PackageCategoryCard/utils';
import Tag, { TagType } from '../../../Model/Tag';
import {
  CheckoutExperience,
  ContractMaxValue,
  ContractMinimumValue,
  ContractProperties,
  ContractStatus,
  ContractType,
  DEFAULT_CHALLENGE_AI_FRIEND_FEE_PERCENTAGE,
  DefaultContract,
  FeeConfig,
  getContractDocContent,
  getAllowedMaxInitialTerm,
  getMaxInitialTerm,
  getSingleFeeFromExisting,
  getOriginalFeesFromContractDoc,
  isContractPricingFree,
  isProgramLongEnoughForChallenge,
} from '../../../utils/userContract';
import { DURATION_UNIT } from '../../../../utils/durationUnit';
import { getOnboardingLinkFromContract, getOnboardingLinkFromLead } from '../../../utils/onboardingLink';
import { generateTerms } from '../../../utils/userContractTerms';
import { ContractState, ContractPriceType } from '../utils/ContractTypes';
import ContractModalContext, { initialValues } from './ContractModalContext';
import texts from './texts.json';

// This constant is used when checking if a term corresponds to a single month.
const ONE_MONTH = 1;
const EMPTY_VALUE = '';

// Default monthly payment value used for challenge contracts
const CHALLENGE_CONTRACT_MONTHLY_PAYMENT_DEFAULT = '0.1';

// Default challenge ID used for preview contracts
const CHALLENGE_ID_PREVIEW = 'preview';

// This is the IS Id that we use when we reonboard a client after the first month.
const REONBOARDING_INSIDE_SALES_ID = isDev ? 'HpVorB48wOP8OeugZHcRfYt88hW2' : 'sMZWMRtSitU2Rqii9n9tiJHlrJm2';

const ContractModalContextProvider = ({
  children,
}) => {
  const [coachId, setCoachId] = useState(initialValues.coachId);
  const [isLoadingData, setIsLoadingData] = useState(initialValues.isLoadingData);
  const [isLoadingContract, setIsLoadingContract] = useState(initialValues.isLoadingContract);
  const isLoading = isLoadingData || isLoadingContract;
  const [coachDoc, setCoachDoc] = useState(initialValues.coachDoc);
  const [externalCoachDoc, setExternalCoachDoc] = useState(initialValues.externalCoachDoc);
  const [products, setProducts] = useState(initialValues.products);
  const [contract, setContract] = useState(initialValues.contract);
  const [contractDoc, setContractDoc] = useState(initialValues.contractDoc);
  const [secondaryContractDoc, setSecondaryContractDoc] = useState(initialValues.secondaryContractDoc);
  const [contractState, setContractState] = useState(ContractState.CONFIGURATION);
  const [isSaving, setIsSaving] = useState(initialValues.isSaving);
  const [lead, setLead] = useState(initialValues.lead);
  const [user, setUser] = useState(initialValues.user);
  const [isReonboardingContract, setIsReonboardingContract] = useState(initialValues.isReonboardingContract);
  const [latestContract, setLatestContract] = useState(initialValues.latestContract);
  const [contractType, setContractType] = useState(initialValues.contractType);
  const [isAmendment, setIsAmendment] = useState(initialValues.isAmendment);
  const [fallbackContractId, setFallbackContractId] = useState('');
  const [supersededContractId, setSupersededContractId] = useState('');
  const [selectedPriceType, setSelectedPriceType] = useState(initialValues.selectedPriceType);
  const [isPreviewTerms, setIsPreviewTerms] = useState(initialValues.isPreviewTerms);
  const [leadConfigDoc, setLeadConfigDoc] = useState(initialValues.leadConfigDoc);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  // Total day-slots of the selected challenge program (its `workouts.length`), synced from ContractConfigurator
  // where programs are loaded. Used by isValidContract to ensure the program covers the full challenge period.
  const [selectedProgramWorkoutDays, setSelectedProgramWorkoutDays] = useState(null);
  const [selectedMealPlanId, setSelectedMealPlanId] = useState('');
  const [acceptUntil, setAcceptUntil] = useState(null);
  const [categoryPackageEntries, setCategoryPackageEntries] = useState(initialValues.categoryPackageEntries);
  // The challenge assigned to the low ticket category, when the coach offers one.
  const [categoryChallengeContract, setCategoryChallengeContract] = useState(null);
  // A challenge saved on the lead, held until the coach's offerings have loaded.
  const [pendingLeadChallengeId, setPendingLeadChallengeId] = useState('');
  const [isSavingChallenge, setIsSavingChallenge] = useState(false);

  // If there is only one product, select it by default. Otherwise, let the IS choose the initial product.
  const [selectedProduct, setSelectedProduct] = useState(products.length === 1 ? products[0] : null);
  const isChallengeContract = !!selectedProduct?.isChallengeProduct;
  const {
    userDoc: loggedInUserDoc,
  } = useContext(LoggedInUserContext);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();

  const {
    isAdmin,
    isInsideSales: isLoggedInUserInsideSales,
    isCoachAssistant: isLoggedInUserCoachAssistant,
    isSupport: isLoggedInUserSupport,
  } = useSessionStore();

  // Only shown for IS users on AC-enabled coaches creating a lead (ContractType.USER) contract.
  const isACEnabled = !!coachDoc?.isACEnabled;

  // Edit mode: fall back to plain selectors if the contract's package/product no longer maps to a known category entry.
  const isCategoryChallengeSelected = !!categoryChallengeContract
    && contract.challengeId === categoryChallengeContract.challengeId;
  /*
   * The BASE contract a selected challenge is sold from. A challenge is bought through a link
   * naming that contract, which exists before any contract is written for the lead.
   */
  const selectedChallengeBaseContractId = isCategoryChallengeSelected ? categoryChallengeContract.id : '';

  /*
   * The contract a challenge is bought through. The coach's own Low Ticket contract once its card
   * is the selection — it wins over any contract already on file, which is the one being replaced —
   * and otherwise the challenge contract being edited directly.
   */
  const challengeLinkContractId = isChallengeContract
    ? (selectedChallengeBaseContractId || contractDoc?.id || '')
    : '';

  /*
   * A contract written for this lead earlier, now that the rep has picked the challenge instead.
   * A challenge is not sold through a per-lead contract, so that contract is no longer the offer:
   * saving the challenge cancels it rather than leaving a second payable link alive.
   */
  const supersededByChallengeContract = isChallengeContract && contractDoc && !contractDoc.challengeId
    ? contractDoc
    : null;

  /*
   * Whether an offering card is the current pick. Coming back in to change the offering clears it
   * until the coach picks again, so the grid highlights nothing — and with no offering on the table
   * the footer offers the ordinary contract actions rather than the challenge ones.
   */
  const [isCardSelectionCleared, setIsCardSelectionCleared] = useState(false);
  const hasCardSelection = !isCardSelectionCleared && (!!contract.packageId || isCategoryChallengeSelected);
  const clearCardSelection = useCallback(() => setIsCardSelectionCleared(true), []);
  const markCardPicked = useCallback(() => setIsCardSelectionCleared(false), []);

  /*
   * Saving the challenge against the lead takes the place of creating a contract, so it is offered
   * whenever a challenge is the pick for a lead — including over a contract already on file. While
   * the cards show with nothing picked there is no challenge to save, even though the one being
   * changed is still loaded.
   */
  const canSaveChallengeForLead = isChallengeContract
    && !!lead?.id
    && !contractDoc?.challengeId
    && hasCardSelection;

  /*
   * The challenge on the table is the one already recorded for this lead. Its link is only worth
   * sending once that is true: the lead has to carry the challenge for the sale to land on them.
   */
  const isSelectedChallengeSaved = !!challengeLinkContractId
    && lead?.selectedChallengeContractId === challengeLinkContractId;

  /*
   * Who the onboarding link goes to. The contract carries a copy of the lead's details once one
   * exists; a challenge is handed over before that, so the lead itself answers for it.
   */
  const onboardingLinkRecipient = useMemo(() => (lead?.id ? {
    leadId: lead.id,
    name: contractDoc?.userName || lead.name,
    email: contractDoc?.userEmail || lead.email,
    phoneNumber: contractDoc?.phoneNumber || lead.phoneNumber,
    coachName: contractDoc?.coachName || coachDoc?.name || '',
  } : null), [lead, contractDoc, coachDoc]);

  /*
   * A challenge is bought through a link naming the contract it is sold from; every other offer is
   * resolved from the lead alone. Either way the leadId rides along, so the sale lands on this lead.
   */
  const onboardingLink = useMemo(() => {
    if (!lead?.id) {
      return '';
    }
    return challengeLinkContractId
      ? getOnboardingLinkFromContract(challengeLinkContractId, lead.id)
      : getOnboardingLinkFromLead(lead.id);
  }, [lead, challengeLinkContractId]);

  const contractMatchesCategoryEntry = useMemo(() => {
    if (!contractDoc) return true;
    // A challenge is sold from its own contract rather than from a package, so it matches on the
    // challenge instead — without this, editing a challenge's terms drops back to the selectors.
    if (categoryChallengeContract && contract.challengeId === categoryChallengeContract.challengeId) {
      return true;
    }
    return categoryPackageEntries.some(
      ({ pkg, product }) => pkg.id === contract.packageId && product.id === selectedProduct?.id,
    );
  }, [
    contractDoc,
    categoryPackageEntries,
    categoryChallengeContract,
    contract.packageId,
    contract.challengeId,
    selectedProduct,
  ]);

  const showCategoryCards = isACEnabled && isLoggedInUserInsideSales && contractType === ContractType.USER
    && contractMatchesCategoryEntry;

  /*
   * The coach is back in the grid choosing a different offering, with the challenge they came in on
   * still loaded. Creating a contract from it would write the lead a challenge contract of their
   * own, which this flow exists to avoid — there is nothing to create until they pick something.
   */
  const isChoosingOffering = showCategoryCards && !!contract.challengeId && !hasCardSelection;

  // The sale is one an Inside Sales rep is making, rather than the coach writing their own.
  const isSoldByInsideSales = !!coachDoc?.isEnrolledInISProgram && isLoggedInUserInsideSales;

  /**
   * Record the rep on the lead they are selling a challenge to.
   *
   * A challenge writes no contract for the lead, so `isAgent` is the only thing naming its seller
   * when the buyer pays — fulfilment reads it to credit the commission. Every other offer writes a
   * contract whose own `sellerId` credits the rep, and `isAgent` is the lead's claimant, which the
   * lead lists and the contracts manager filter by: rewriting it there would reassign the lead as a
   * side effect of saving a contract, for nothing the server reads.
   *
   * Only for Inside Sales reps on IS-enrolled coaches, since `isAgent` names an IS rep; a coach or
   * an assistant selling for themselves is not one, and letting their id in would credit the
   * commission to the wrong person.
   *
   * Never fatal: the sale is already recorded, so a failure here costs the attribution, not the sale.
   */
  const recordLeadAgent = useCallback(async () => {
    if (!isSoldByInsideSales || !lead?.id || isReonboardingContract) {
      return;
    }
    try {
      await lead.setInsideSalesAgent(loggedInUserDoc.id, loggedInUserDoc.name);
    } catch (error) {
      Sentry.captureException(error, { extra: { leadId: lead.id, agentId: loggedInUserDoc.id } });
    }
  }, [isSoldByInsideSales, lead, isReonboardingContract, loggedInUserDoc]);

  /*
    Products already held live, kept out of the selector: one live contract per product per
    coach is enforced server-side on accept, so offering one only produces a later rejection.
    The superseded contract's own product stays in - amending a contract on its own product
    is the normal case.
  */
  const blockedProductIds = useMemo(() => {
    // `user` starts as a plain `{}` from the context defaults, and leads have no client.
    if (!user?.id) {
      return [];
    }
    /*
      By id, not coach-scoped: an entry seeded at checkout carries no `coachId` until activation
      adds it, so the scoped lookup misses a contract mid-payment and leaves its own product in
      the blocklist. Ownership is already established — the modal was opened from this coach's
      contract row, the same basis `buildBillingTargets` adopts those entries on.
    */
    const supersededProductId = supersededContractId
      ? user.contractEntryById(supersededContractId)?.productId
      : undefined;
    return (user.activeProductIds || []).filter((productId) => productId !== supersededProductId);
  }, [user, supersededContractId]);

  useEffect(() => {
    // Load the coach data here since this modal can now be used with leads coming from different sources.
    const loadCoachData = async () => {
      setIsLoadingData(true);
      const coachDocument = await Coach.getCoachById(coachId);
      const externalCoach = new ExternalCoach(coachId);
      await externalCoach.init();

      const externalCoachProducts = await externalCoach.getProducts();

      // Load discount offer config
      const leadConfig = new LeadConfig();
      await leadConfig.init();

      // Load packages in the same sequence to keep isLoadingData true — prevents a flash of the product selector.
      let packageEntries = [];
      let challengeContract = null;
      if (coachDocument.isACEnabled) {
        /*
         * The offerings only decide which cards the configurator shows, so a failure here costs the
         * coach the shortcut, not the modal. Swallowed deliberately: the challenge query needs a
         * composite index, and letting a missing one escape would leave this effect without its
         * state updates and hang the modal on its loader for every AC-enabled coach.
         */
        try {
          const eligibleProducts = externalCoachProducts.filter((p) => !p.isArchived && !p.isChallengeProduct);
          const results = await Promise.all(
            eligibleProducts.map(async (product) => {
              const col = await product.getPackages();
              return col.docs
                .filter((pkg) => !pkg.isArchived && pkg.category)
                .map((pkg) => ({ pkg, product }));
            }),
          );
          packageEntries = results.flat();

          // A challenge is offered through its BASE contract, so it comes from the contracts side
          // rather than from a product. Only the low ticket category holds one. It is kept once its
          // accept-until date passes, so the card can show the offering as closed instead of hiding it.
          const categorizedChallenges = await UserContract.getCategorizedChallengeContractsByCoach(coachId);
          const lowTicketPackage = packageEntries.some(({ pkg }) => pkg.category === CHALLENGE_CATEGORY);
          /*
           * A category holds one offering, so a package and a challenge both carrying low ticket is
           * data written outside this flow. The offerings table lets the package win; matching that
           * here keeps the rep from selling a challenge the configuration says is not assigned.
           */
          challengeContract = lowTicketPackage
            ? null
            : categorizedChallenges.find(({ category }) => category === CHALLENGE_CATEGORY) || null;
        } catch (error) {
          Sentry.captureException(error, { extra: { coachId, context: 'loadCoachData offerings' } });
        }
      }

      if (isComponentMountedRef.current) {
        setCoachDoc(coachDocument);
        setExternalCoachDoc(externalCoach);
        setProducts(externalCoachProducts);
        setLeadConfigDoc(leadConfig);
        setCategoryPackageEntries(packageEntries);
        setCategoryChallengeContract(challengeContract);
        setContract((currentContract) => ({
          ...currentContract,
          coachConditions: coachDocument.termsAndConditionsText,
          currency: externalCoach.defaultCurrency || DefaultContract.currency,
        }));
        setIsLoadingData(false);
      }
    };

    if (coachId) {
      loadCoachData();
    }
  }, [
    coachId,
    isComponentMountedRef,
  ]);

  // Effect that loads and updates context data when it's a new amendment.
  useEffect(() => {
    /*
      `supersededContractId` is seeded by the modal content's own mount effect, so this can run
      before it arrives and again after. The two runs issue different query counts, so only the
      latest is allowed to write - otherwise an arbitrary current contract can land last.
    */
    let isCurrentRequest = true;
    const setDataForAmendment = async () => {
      const [currentContract, currentSubscription] = await Promise.all([
        supersededContractId
          ? UserContract.getContractById(supersededContractId)
          : UserContract.getCurrentContractByUserId(user.id),
        supersededContractId
          ? Subscription.getCurrentSubscriptionByContractId(user.id, supersededContractId)
          : Subscription.getCurrentSubscription(user.id),
      ]);
      // For a new amendment contract, we prepopulate the product from the current contract.
      if (isComponentMountedRef.current && isCurrentRequest) {
        setFallbackContractId(currentContract?.id || '');
        if (currentContract && !!currentContract.productId) {
          const filteredProduct = products.filter((product) => (product.id === currentContract.productId));
          setSelectedProduct(filteredProduct.length ? filteredProduct[0] : null);
        }
        // If the subscription is active, we set the start date to the end of the current period.
        const startDate = currentSubscription?.subscriptionData?.status === StripeSubscriptionState.ACTIVE
          ? moment.unix(currentSubscription.currentPeriodEnd)
          : null;
        setContract((prevContract) => ({
          ...prevContract,
          [ContractProperties.START_DATE]: startDate,
        }));
      }
    };

    if (!isLoading && isAmendment && !contract.createdAt) {
      setDataForAmendment();
    }
    return () => { isCurrentRequest = false; };
  }, [
    isComponentMountedRef,
    isAmendment,
    user,
    products,
    isLoading,
    contract.createdAt,
    supersededContractId,
  ]);

  /*
    Check whether the user is reonboarding after the first month. This is a special case, and we assign different
    values to the sellerId and the IS fee.
  */
  const isWithinFirstMonth = useMemo(() => (
    user && moment().isBefore(moment(user.serviceStartAt).add(ONE_MONTH, 'month'))
  ), [user]);

  const isReonboardingByACOrSupport = isReonboardingContract
    && (isLoggedInUserCoachAssistant || isLoggedInUserSupport);

  const getContractFees = useCallback((product = null) => {
    if (!coachDoc) {
      return {};
    }

    // Get the configured values from the coach document. These are the default values.
    const {
      feesConfig: {
        baseFee: coachBaseFee,
        assistantCoachFee: coachAssistantCoachFee,
        insideSalesFee: coachInsideSalesFee,
      },
      isEnrolledInISProgram,
    } = coachDoc;

    // If there is no coachBaseFee, get baseFee from passed product as param.
    // This allows getting fees and updating the product in the same event loop cycle.
    // Otherwise - get it from selectedProduct state.
    const baseFee = coachBaseFee?.value || product?.baseFee || selectedProduct?.baseFee;
    // When a product is explicitly passed, use only its flags
    // to avoid reading stale selectedProduct state
    const isAiFriendContract = product !== null
      ? !!product.isAiFriendEnabled
      : !!selectedProduct?.isAiFriendEnabled;
    const isChallenge = product !== null ? !!product?.isChallengeProduct : isChallengeContract;

    // All non-challenge/non-AI Friend contracts must have a base fee configured.
    if (typeof baseFee === 'undefined' && !isChallenge && !isAiFriendContract) {
      Sentry.captureMessage('Coach is missing base fee configuration', {
        extra: {
          coach: coachDoc.id,
        },
      });
    }

    if (isChallenge) {
      return {
        [ContractProperties.CHALLENGE_FEE]: DEFAULT_CHALLENGE_AI_FRIEND_FEE_PERCENTAGE,
      };
    }

    if (isAiFriendContract) {
      return {
        [ContractProperties.AI_FRIEND_FEE]: DEFAULT_CHALLENGE_AI_FRIEND_FEE_PERCENTAGE,
      };
    }

    const isFirstMonthReonboardingWithISFee = isReonboardingByACOrSupport
      && isWithinFirstMonth
      && !!latestContract?.fees?.insideSalesFee?.value;

    /*
      There are four cases where we should add the IS fee to the contract state:
        1. The coach is enrolled in the IS program and the logged in user is an IS agent.
        2. The logged in user is an AC or a support agent creating a reonboarding contract after
           the first month, for a coach enrolled in the IS program.
        3. An AC or a support agent is reonboarding a user within their first month and the
           original contract had an IS fee.
        4. When creating reusable contracts for IS enrolled coaches.
    */
    const shouldAddISFee = (isEnrolledInISProgram && isLoggedInUserInsideSales)
      || (isReonboardingByACOrSupport && !isWithinFirstMonth && isEnrolledInISProgram)
      || isFirstMonthReonboardingWithISFee
      || (isEnrolledInISProgram && contractType === ContractType.BASE);

    // Calculate the IS fee value based on the configuration and type of contract created.
    const ISFeeValue = isFirstMonthReonboardingWithISFee
      ? latestContract?.fees?.insideSalesFee?.value
      : coachInsideSalesFee?.value || FeeConfig.INSIDE_SALES.value;

    /*
      Set coach fees initial values. If AC or IS are not enabled for this coach, we won't add
      AC or IS fees to the contract state.
    */
    const contractFeesValues = {
      [ContractProperties.BASE_FEE]: baseFee,
      ...(isACEnabled
        ? { [ContractProperties.AC_FEE]: coachAssistantCoachFee?.value || FeeConfig.ASSISTANT_COACH.value }
        : {}),
      ...(shouldAddISFee
        ? { [ContractProperties.IS_FEE]: ISFeeValue }
        : {}),
    };

    return contractFeesValues;
  }, [
    coachDoc,
    selectedProduct,
    isACEnabled,
    isLoggedInUserInsideSales,
    isWithinFirstMonth,
    latestContract,
    isReonboardingByACOrSupport,
    contractType,
    isChallengeContract,
  ]);

  // Update contract fees and challengeId when selected product changes
  useEffect(() => {
    if (!coachDoc) return;

    setContract((prevContract) => {
      const { [ContractProperties.CHALLENGE_FEE]: prevChallengeFee } = prevContract;
      const wasChallengeContract = prevChallengeFee !== undefined;

      // Only act when the challenge state is actually changing; otherwise preserve contract as-is.
      if (isChallengeContract === wasChallengeContract) {
        return prevContract;
      }

      const {
        [ContractProperties.BASE_FEE]: _baseFee,
        [ContractProperties.AC_FEE]: _acFee,
        [ContractProperties.IS_FEE]: _isFee,
        [ContractProperties.CHALLENGE_FEE]: _challengeFee,
        [ContractProperties.AI_FRIEND_FEE]: _aiFriendFee,
        [ContractProperties.CHALLENGE_ID]: _challengeId,
        ...contractWithoutFees
      } = prevContract;

      if (!isChallengeContract) {
        // For existing contracts restore from the contract doc; for new ones use coach defaults.
        // If the current product is AI Friend, onProductChange already set the correct fee — skip.
        const originalFeesFromContractDoc = contractDoc
          ? getOriginalFeesFromContractDoc(contractDoc)
          : null;
        const restoredFees = originalFeesFromContractDoc && Object.keys(originalFeesFromContractDoc).length
          ? originalFeesFromContractDoc
          : getContractFees();

        if (!contractDoc && ContractProperties.AI_FRIEND_FEE in restoredFees) return prevContract;

        /*
          Only challenges can be sold in weeks — a regular subscription is billed by Stripe on a monthly interval,
          so a week unit left over from a challenge would make its term mean something nothing honors. Reset it.
        */
        return {
          ...contractWithoutFees,
          ...restoredFees,
          [ContractProperties.DURATION_UNIT]: DURATION_UNIT.MONTH,
        };
      }

      // Switching TO challenge: derive from existing fees if updating, fresh defaults if creating.
      const newFees = contractDoc
        ? getSingleFeeFromExisting(prevContract, ContractProperties.CHALLENGE_FEE)
        : getContractFees();

      return {
        ...contractWithoutFees,
        ...newFees,
        [ContractProperties.CHALLENGE_ID]: CHALLENGE_ID_PREVIEW,
        [ContractProperties.CANCEL_AT_PERIOD_END]: true,
        [ContractProperties.MONTHLY_PAYMENT]: CHALLENGE_CONTRACT_MONTHLY_PAYMENT_DEFAULT,
        [ContractProperties.MIN_SUBSCRIPTION_MONTHS]:
          contractWithoutFees[ContractProperties.INITIAL_TERM] || contractWithoutFees.initialTerm,
      };
    });
  }, [isChallengeContract, coachDoc, getContractFees, contractDoc]);

  const resetContract = useCallback(() => {
    const contractFees = getContractFees();
    // Keep contract name, seller, and package values on reset
    setContract(({
      [ContractProperties.NAME]: currentName,
      [ContractProperties.SELLER_ID]: currentSellerId,
      [ContractProperties.START_DATE]: currentStartDate,
      [ContractProperties.PACKAGE_ID]: currentPackageId,
      [ContractProperties.PACKAGE_NAME]: currentPackageName,
    }) => ({
      ...DefaultContract,
      ...contractFees,
      coachConditions: coachDoc?.termsAndConditionsText,
      currency: externalCoachDoc?.defaultCurrency || DefaultContract.currency,
      [ContractProperties.NAME]: currentName || DefaultContract[ContractProperties.NAME],
      [ContractProperties.SELLER_ID]: currentSellerId,
      ...(currentStartDate
        ? { [ContractProperties.START_DATE]: currentStartDate }
        : DefaultContract[ContractProperties.START_DATE]
      ),
      ...(currentPackageId ? { packageId: currentPackageId, packageName: currentPackageName } : {}),
      ...(isChallengeContract && {
        [ContractProperties.CHALLENGE_ID]: CHALLENGE_ID_PREVIEW,
        [ContractProperties.CANCEL_AT_PERIOD_END]: true,
      }),
    }));

    // Reset challenge-related states
    setSelectedProgramId('');
    setSelectedProgramWorkoutDays(null);
    setSelectedMealPlanId('');
    setAcceptUntil(null);
  }, [
    coachDoc,
    externalCoachDoc,
    getContractFees,
    isChallengeContract,
  ]);

  const loadContractByLead = useCallback(async (leadObj) => {
    if (leadObj.id) {
      /*
        Opening a lead starts fresh. The modal outlives the lead where it is a panel rather than a
        dialog — the SDR inbox keeps one mounted while the rep clicks down the list — so every
        per-lead state has to be dropped here, not just the ones the next lead happens to overwrite.

        The contract pair matters most: the load below only ASSIGNS it, so a lead with no contract
        of their own would otherwise keep showing the previous lead's. Saving a challenge then
        cancels that contract as superseded — on the wrong lead.
      */
      setIsCardSelectionCleared(false);
      setContractDoc(null);
      setSecondaryContractDoc(null);
      setIsLoadingContract(true);
      const recentContract = await UserContract.getContractByLeadId(leadObj.id);

      let primaryContract = null;
      let secondaryContract = null;
      //  If the recent contract is the secondary contract, we need to load the primary contract.
      if (recentContract && recentContract.primaryContractId) {
        primaryContract = await UserContract.getContractById(recentContract.primaryContractId);
        secondaryContract = recentContract;
      } else if (recentContract) {
        // Else, we just set the recent contract as the primary contract.
        primaryContract = recentContract;
      }

      /*
       * Swapping the lead onto a challenge cancels the contract that was written for them, and the
       * lookup above is by recency rather than by status — so without this the cancelled contract
       * comes back as the offer on file and hides the challenge the rep actually sold.
       */
      if (primaryContract?.status === ContractStatus.CANCELED && leadObj.selectedChallengeContractId) {
        primaryContract = null;
        secondaryContract = null;
      }

      if (isComponentMountedRef.current) {
        if (primaryContract) {
          setContractDoc(primaryContract);
          setContractType(primaryContract.type);
          setSecondaryContractDoc(secondaryContract);
        } else {
          setPendingLeadChallengeId(leadObj.selectedChallengeContractId || '');
          setIsLoadingContract(false);
        }

        setLead(leadObj);
      }
    } else {
      showToast(texts.leadIdError, { error: true });
    }
  }, [
    isComponentMountedRef,
    showToast,
  ]);

  /*
    `productId` scopes which existing draft may prefill the form. A client can hold several
    products at once, so the newest PENDING/IN_PROGRESS contract is often for a different one;
    loading it would show another product's terms under the product the coach chose. Absent
    (plain re-onboarding), every draft is a candidate, as before. A draft carrying no product is
    kept for any product, matching the coverage rule applied everywhere else: before attribution
    it belonged to the client's only one, so excluding it would blank a legacy client's form.
  */
  const loadReonboardingContractByUser = useCallback(async (userObj, productId = '') => {
    if (userObj.id) {
      setIsLoadingContract(true);
      const existingUserReonboardingContracts = await UserContract
        .getReonboardingContractsCollectionForUser(userObj.id);

      /*
        If this is the first time we are reonboarding this user, we have to get the latest contract to extract some
        info we'll need after.
      */
      if (!existingUserReonboardingContracts.size) {
        /*
          Get the list of contracts that were previously created for this user. We may have to extract the fees
          configuration that was set on the latest one.
        */
        const userContracts = await UserContract.getContractsByUserAndCoach(userObj.id, coachId);
        const [latestContractDoc] = userContracts.docs.slice()
          .filter(({ status }) => status !== ContractStatus.PENDING && status !== ContractStatus.IN_PROGRESS)
          .sort((a, b) => b.createdAt - a.createdAt);

        if (latestContractDoc && isComponentMountedRef.current) {
          setLatestContract(latestContractDoc);
        }
      }

      autorun(() => {
        if (isComponentMountedRef.current) {
          const sortedReonboarding = [...existingUserReonboardingContracts.docs]
            .filter((doc) => !productId || !doc.productId || doc.productId === productId)
            .sort((a, b) => b.createdAt - a.createdAt);
          if (sortedReonboarding.length) {
            const existingUserReonboardingContract = sortedReonboarding[0];
            setContractDoc(existingUserReonboardingContract);
            setContractType(existingUserReonboardingContract.type);
          } else {
            setIsLoadingContract(false);
          }
          setIsReonboardingContract(true);
          setUser(userObj);
        }
      });
    } else {
      showToast(texts.userIdError, { error: true });
    }
  }, [
    coachId,
    isComponentMountedRef,
    showToast,
  ]);

  const loadContractFromDoc = useCallback((contractDocument) => {
    const {
      startDate: stringDate,
      initialPaymentInCents,
      totalPriceInCents,
      postCommitmentPriceInCents,
      upfrontPaymentInCents,
      initialTerm,
      minSubscriptionMonths,
      currency,
      productId,
      priceId,
      monthlyPriceId,
      packageId,
      packageName,
      cancelAtPeriodEnd,
      recurringBillingMonths,
      coachConditions,
      version,
      type,
      createdAt,
      updatedAt,
      sellerId,
      primaryContractId,
      name = DefaultContract[ContractProperties.NAME],
      challengeId,
      acceptUntil: acceptUntilDate,
      upsellProductId,
      upsellMonthlyPriceInCents,
      /*
        Absent on every contract stored before Stripe Hosted Checkout existed. Left
        undefined, the configurator's auto-default effect reads it as unset and writes
        STRIPE_HOSTED, so the next unrelated edit — a name, a fee, a terms tweak —
        would persist a checkout method the coach never chose and silently move an
        existing DPL off Avida-managed. Defaulting here, as getContractObj already
        does, keeps the Stripe default for contracts that never had a value.
      */
      checkoutExperience = CheckoutExperience.AVIDA_MANAGED,
      /*
        Must be carried onto the loaded contract. Everything downstream reads the
        unit off this object — the configurator's labels, the commitment dates,
        the regenerated terms, and getContractDocContent on save. Dropping it
        here doesn't just mislabel the form: saving would then write
        durationUnit 'month' over a stored 'week', turning a 6-week challenge
        into a 6-month one and granting six months of access.
      */
      durationUnit,
      // Carried for the same reason: a field left off the loaded contract is saved back as off.
      isStoryShareEnabled,
      fees: {
        baseFee: { value: baseFee } = {},
        assistantCoachFee: { value: assistantCoachFee } = {},
        insideSalesFee: { value: insideSalesFee } = {},
        challengeFee: { value: challengeFee } = {},
        aiFriendFee: { value: aiFriendFee } = {},
      } = {},
    } = contractDocument;

    const filteredProduct = products.filter((product) => (product.id === productId));
    const selectedProductFromContract = filteredProduct.length ? filteredProduct[0] : null;
    const isAiFriendContract = !!selectedProductFromContract?.isAiFriendEnabled;

    // If baseFee is missing we should report it to sentry
    if (typeof baseFee === 'undefined' && !challengeId && !isAiFriendContract) {
      Sentry.captureMessage('Contract is missing base fee', {
        extra: {
          contractId: contractDocument.id,
          coachId: contractDocument.coach,
        },
      });
    }

    /*
      If there is an existing contract, we need to set the contract state to DETAILS, so that we can display
      the contract terms.
    */
    setContractState(ContractState.DETAILS);
    setSelectedProduct(selectedProductFromContract);

    /*
      Calculate the upfront amount based on the initial payment and the upfront payment. This will be set to 0 if the
      startDate is not in the future.
    */
    let upfrontPayment = 0;
    if (isFutureDay(stringDate)) {
      upfrontPayment = formatAmoutInCents(upfrontPaymentInCents, MONEY_DECIMALS);
    }

    setContract({
      startDate: moment(stringDate),
      initialPayment: formatAmoutInCents(initialPaymentInCents, MONEY_DECIMALS),
      monthlyPayment: formatAmoutInCents(totalPriceInCents, MONEY_DECIMALS),
      upfrontPayment,
      initialTerm,
      minSubscriptionMonths,
      durationUnit,
      currency,
      priceId,
      monthlyPriceId,
      packageId,
      packageName,
      cancelAtPeriodEnd,
      recurringBillingMonths,
      coachConditions,
      version,
      createdAt,
      updatedAt,
      baseFee,
      sellerId,
      name,
      /*
        A challenge is sold on Stripe Hosted Checkout unless Avida-managed was picked for it, so the
        Avida-managed default above must not stand in for that pick.
      */
      checkoutExperience: (
        selectedProductFromContract?.isChallengeProduct && !contractDocument.isAvidaManagedCheckoutPicked
      ) ? CheckoutExperience.STRIPE_HOSTED : checkoutExperience,
      isStoryShareEnabled,
      ...(primaryContractId ? { primaryContractId } : {}),
      ...(insideSalesFee ? { insideSalesFee } : {}),
      ...(assistantCoachFee ? { assistantCoachFee } : {}),
      ...(challengeFee ? { challengeFee } : {}),
      ...(aiFriendFee ? { aiFriendFee } : {}),
      ...(challengeId ? { challengeId } : {}),
      ...(acceptUntilDate ? { acceptUntil: moment(acceptUntilDate.toDate()) } : {}),
      ...(upsellProductId ? { upsellProductId } : {}),
      [ContractProperties.UPSELL_MONTHLY_PRICE]: upsellMonthlyPriceInCents
        ? formatAmoutInCents(upsellMonthlyPriceInCents, MONEY_DECIMALS)
        : DefaultContract[ContractProperties.UPSELL_MONTHLY_PRICE],
      postCommitmentPayment: postCommitmentPriceInCents
        ? formatAmoutInCents(postCommitmentPriceInCents, MONEY_DECIMALS)
        : DefaultContract[ContractProperties.POST_COMMITMENT_PAYMENT],
    });
    setContractType(type);
    setIsLoadingContract(false);

    // If this is a challenge contract, load challenge config
    if (challengeId) {
      if (acceptUntilDate) {
        setAcceptUntil(moment(acceptUntilDate.toDate()));
      }

      // Load the challenge config to get program and meal plan IDs
      (async () => {
        try {
          const challengeConfig = await ChallengeConfig.getChallengeConfigById(challengeId);
          if (challengeConfig) {
            setSelectedProgramId(challengeConfig.programId);
            setSelectedMealPlanId(challengeConfig.mealPlanId);
          }
        } catch (error) {
          Sentry.captureException(new Error('Error loading challenge config'), {
            extra: {
              challengeId,
              error,
            },
          });
        }
      })();
    } else {
      setSelectedProgramId('');
      setSelectedProgramWorkoutDays(null);
      setSelectedMealPlanId('');
      setAcceptUntil(null);
    }
  }, [
    products,
  ]);

  /**
   * Seed the contract being written from the coach's low ticket challenge.
   *
   * The challenge is defined by a BASE contract that acts as the template, but the contract the rep
   * is creating for the lead is still a USER contract. loadContractFromDoc adopts the template's
   * type, so it is restored here — otherwise the modal treats this as editing a base contract and
   * drops the category cards on the way back from the terms view.
   */
  const selectCategoryChallenge = useCallback((challengeBaseContract) => {
    loadContractFromDoc(challengeBaseContract);
    setContractType(ContractType.USER);
  }, [loadContractFromDoc]);

  /*
   * A lead can carry a challenge with no contract behind it yet, so the modal opens on that
   * challenge the way it would open on a contract. Resolved once the coach's offerings are in; if
   * the coach has since moved Low Ticket elsewhere or the challenge has closed, the stale choice is dropped.
   */
  useEffect(() => {
    if (!pendingLeadChallengeId || !categoryChallengeContract) {
      return;
    }
    const isStillOffered = categoryChallengeContract.id === pendingLeadChallengeId
      && isChallengeStillAccepting(categoryChallengeContract);
    if (isStillOffered) {
      selectCategoryChallenge(categoryChallengeContract);
    }
    setPendingLeadChallengeId('');
  }, [pendingLeadChallengeId, categoryChallengeContract, selectCategoryChallenge]);

  /**
   * Save the challenge the rep picked against the lead, without creating a contract.
   *
   * The challenge is sold through its own onboarding link, so nothing is written for the lead
   * until they pay; this only records the choice so the lead reopens on it.
   */
  const saveChallengeForLead = useCallback(async () => {
    if (!lead?.id || !contract.challengeId) {
      return;
    }
    setIsSavingChallenge(true);
    try {
      /*
       * Usually the coach's own Low Ticket challenge, but a rep can be looking at a challenge that
       * has since been unassigned or closed, so fall back to the challenge's own base contract.
       */
      let challengeContractId = categoryChallengeContract?.challengeId === contract.challengeId
        ? categoryChallengeContract.id
        : '';
      if (!challengeContractId) {
        const baseContract = await UserContract.getBaseContractByChallengeId(contract.challengeId);
        challengeContractId = baseContract?.id || '';
      }
      if (!challengeContractId) {
        showToast(texts.challengeSaveError, { error: true });
        return;
      }
      await lead.setSelectedChallengeContract(challengeContractId);

      /*
       * The lead is on the challenge now, so a contract written for them before is no longer an
       * offer — cancel it instead of leaving a second payable link alive. Done after the lead
       * write: a failure here leaves the rep with the challenge saved and a stale contract they
       * can still see and cancel, rather than a lead holding neither.
       */
      if (supersededByChallengeContract) {
        await supersededByChallengeContract.updateStatus(ContractStatus.CANCELED);
        // The secondary contract is the same offer's second half, so it goes with it.
        if (secondaryContractDoc) {
          await secondaryContractDoc.updateStatus(ContractStatus.CANCELED);
        }
        if (isComponentMountedRef.current) {
          setContractDoc(null);
          setSecondaryContractDoc(null);
        }
      }

      /*
       * The only step that names the rep for this sale: no contract is written until the lead pays,
       * and the one minted then inherits the challenge's own seller — the coach — unless the lead
       * can say who sold it.
       */
      await recordLeadAgent();

      showToast(texts.challengeSaved, { type: 'success' });
    } catch (error) {
      Sentry.captureException(error, { extra: { leadId: lead.id } });
      if (isComponentMountedRef.current) {
        showToast(texts.challengeSaveError, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsSavingChallenge(false);
      }
    }
  }, [
    lead,
    contract.challengeId,
    categoryChallengeContract,
    supersededByChallengeContract,
    secondaryContractDoc,
    showToast,
    isComponentMountedRef,
    recordLeadAgent,
  ]);

  const loadReusableContractFromDoc = useCallback((selectedContractDoc) => {
    if (selectedContractDoc.id) {
      setIsLoadingContract(true);
      if (isComponentMountedRef.current) {
        loadContractFromDoc(selectedContractDoc);
        setContractDoc(selectedContractDoc);
        setContractType(selectedContractDoc.type);
        setIsLoadingContract(false);
      }
    } else {
      showToast(texts.contractIdError, { error: true });
    }
  }, [
    isComponentMountedRef,
    loadContractFromDoc,
    showToast,
  ]);

  useEffect(() => {
    if (products && products.length && contractDoc) {
      loadContractFromDoc(contractDoc);
    }
  }, [
    products,
    contractDoc,
    loadContractFromDoc,
    isComponentMountedRef,
  ]);

  // Term on the loaded document, which raises the save-time ceiling for contracts stored above the cap.
  const storedInitialTerm = contractDoc?.initialTerm;

  const isValidContract = useMemo(() => {
    const {
      startDate,
      initialPayment,
      initialTerm,
      minSubscriptionMonths,
      monthlyPayment,
      recurringBillingMonths,
      sellerId,
      baseFee,
      assistantCoachFee = 0,
      insideSalesFee = 0,
      challengeFee,
      aiFriendFee,
      upfrontPayment = 0,
      postCommitmentPayment,
      upsellProductId,
      upsellMonthlyPrice,
      durationUnit,
    } = contract;

    const isFeeValid = (fee) => fee <= ContractMaxValue.FEE_PERCENTAGE && fee >= ContractMinimumValue.FEE_PERCENTAGE;

    const isDateValid = startDate?.isValid() && startDate.isAfter(moment().startOf('day').subtract(1, 'day'));

    // We just validate if a seller is sellected when the contract is a BASE contract
    const isSellerValid = contractType !== ContractType.BASE || !!sellerId;

    const isAiFriendContract = !!selectedProduct?.isAiFriendEnabled;

    // For challenge/AI Friend contracts, validate challengeFee/aiFriendFee;
    // for regular contracts, validate standard fees
    const areFeesDefined = (() => {
      if (isChallengeContract) return typeof challengeFee !== 'undefined';
      if (isAiFriendContract) return typeof aiFriendFee !== 'undefined';
      return typeof baseFee !== 'undefined';
    })();

    // areFeesDefined ensures the fee exists, so we can safely use it without defaults
    const areFeeValuesValid = (() => {
      if (isChallengeContract) return isFeeValid(challengeFee);
      if (isAiFriendContract) return isFeeValid(aiFriendFee);
      return isFeeValid(baseFee) && isFeeValid(assistantCoachFee) && isFeeValid(insideSalesFee)
        && isFeeValid(baseFee + assistantCoachFee + insideSalesFee);
    })();

    const isProgramRequired = isChallengeContract && !!selectedProduct?.isVideoFeedBackEnabled;
    const isMealPlanRequired = isChallengeContract && !!selectedProduct?.isMealPlanEnabled;
    const isChallengeConfigValid = !isChallengeContract
      || (
        (!isProgramRequired || !!selectedProgramId)
        && (!isMealPlanRequired || !!selectedMealPlanId)
        && !!acceptUntil
        && !!contract.name?.trim()
      );

    /*
      When a challenge has a program selected, it must contain enough day-slots to cover the full
      challenge period (initialTerm, counted in durationUnit); otherwise participants only get the days
      the program contains.
    */
    const isChallengeProgramLongEnough = !isChallengeContract
      || !selectedProgramId
      || selectedProgramWorkoutDays === null
      || isProgramLongEnoughForChallenge(selectedProgramWorkoutDays, initialTerm, durationUnit, startDate);

    // Validate upsell configuration, if we have a product we need to have a monthly price,
    const isUpsellConfigValid = !upsellProductId || (upsellProductId && upsellMonthlyPrice && upsellMonthlyPrice > 0);

    /*
      Admins may mint fully free ONE_TIME contracts (S2-8767) — comped links
      that skip payment on the onboarding page. Requires explicitly typed
      zeros, so an untouched form can't slip through as free.
    */
    const isFreeOneTimeByAdmin = isAdmin
      && contractType === ContractType.ONE_TIME
      && isContractPricingFree(contract);

    const isValid = !!selectedProduct
      && isDateValid
      && minSubscriptionMonths >= ContractMinimumValue.MIN_SUBSCRIPTION_MONTHS
      && minSubscriptionMonths >= recurringBillingMonths
      && (minSubscriptionMonths % recurringBillingMonths === 0)
      && (minSubscriptionMonths >= initialTerm)
      // allow 0 monthly payment for test runs and admin-minted free one-time contracts
      && (Number(monthlyPayment) > 0 || isTestRun || isFreeOneTimeByAdmin)
      && initialTerm > 0
      /*
        The term input caps this, but a typed value can still exceed the unit's
        ceiling. Contracts stored above the cap keep their own value as the
        ceiling so an existing document does not become unamendable.
      */
      && initialTerm <= getAllowedMaxInitialTerm(durationUnit, storedInitialTerm)
      // allow 0 initial payment for test runs, AI friend and admin-minted free one-time contracts
      && (Number(initialPayment) > 0 || isTestRun || selectedProduct.isAiFriendEnabled || isFreeOneTimeByAdmin)
      && isSellerValid
      && areFeesDefined
      && areFeeValuesValid
      && isChallengeConfigValid
      && isChallengeProgramLongEnough
      && Number(upfrontPayment) <= Number(initialPayment)
      && (postCommitmentPayment === EMPTY_VALUE || Number(postCommitmentPayment) > 0)
      && isUpsellConfigValid;
    return isValid;
  }, [
    contract,
    contractType,
    selectedProduct,
    isChallengeContract,
    isAdmin,
    selectedProgramId,
    selectedProgramWorkoutDays,
    acceptUntil,
    selectedMealPlanId,
    storedInitialTerm,
  ]);

  const onContractFieldChange = useCallback((name, value) => {
    let newContractValues = {};

    switch (name) {
      case ContractProperties.INITIAL_PAYMENT:
        // We save the initial payment.
        newContractValues[ContractProperties.INITIAL_PAYMENT] = value;

        /*
          If the initial term is 1, then we are configuring a monthly contract, so we set the same value to the
          monthly payment. The monthly payment can then be modified independently.

          Challenges sync regardless of term. They are billed once up front, so their totalPriceInCents (which is
          what the monthly payment feeds, and what the one-time Stripe checkout charges) must always equal the
          initial payment. The monthly field is hidden for challenges and seeded to a placeholder, so without this
          a coach who set the term before the price would leave that placeholder in place as the charged amount.
        */
        if (contract[ContractProperties.INITIAL_TERM] === ONE_MONTH || isChallengeContract) {
          newContractValues[ContractProperties.MONTHLY_PAYMENT] = value;
        }
        break;
      case ContractProperties.INITIAL_TERM:
        newContractValues[ContractProperties.INITIAL_TERM] = Number(value);
        /*
          When the initial term changes to 1, we have to configure the monthly payment to be the same as the initial
          term payment. We take the current value of the contract and assign it to the monthly payment.
        */
        if (newContractValues[ContractProperties.INITIAL_TERM] === ONE_MONTH) {
          newContractValues[ContractProperties.MONTHLY_PAYMENT] = contract[ContractProperties.INITIAL_PAYMENT];
        }
        // For challenge contracts, commitment should always match initial term
        if (isChallengeContract) {
          newContractValues[ContractProperties.MIN_SUBSCRIPTION_MONTHS] = Number(value);
          // Keep the charged amount pinned to the initial payment — see the INITIAL_PAYMENT case above.
          newContractValues[ContractProperties.MONTHLY_PAYMENT] = contract[ContractProperties.INITIAL_PAYMENT];
        }
        break;
      case ContractProperties.DURATION_UNIT: {
        newContractValues[ContractProperties.DURATION_UNIT] = value;
        /*
          Each unit has its own ceiling (52 weeks / 12 months), so switching to a
          shorter-ceilinged unit has to pull the term down with it — otherwise a
          52-week challenge would silently become a 52-month one. The commitment
          mirrors the term on challenges, which are the only contracts that can
          carry a non-month unit.
        */
        const maxTerm = getMaxInitialTerm(value);
        const clampedTerm = Math.min(contract[ContractProperties.INITIAL_TERM], maxTerm);
        newContractValues[ContractProperties.INITIAL_TERM] = clampedTerm;
        newContractValues[ContractProperties.MIN_SUBSCRIPTION_MONTHS] = clampedTerm;
        break;
      }
      case ContractProperties.START_DATE:
        // Save the start date without any modifications in the contract object.
        newContractValues = {
          [name]: value,
        };

        /*
          If the new startDate is not a future startDate, then we set the upfront payment to 0, since the client will
          have to pay the full amount the moment they subscribe.

          Compared by day: the picker hands back the chosen day at the current clock time, which an
          instant comparison against the start of today reads as the future — leaving the advance in
          place on a contract that starts immediately, in a section the configurator then hides.
        */
        if (!isFutureDay(value)) {
          newContractValues[ContractProperties.UPFRONT_PAYMENT] = 0;
        }
        break;
      case ContractProperties.UPFRONT_PAYMENT:
      case ContractProperties.MONTHLY_PAYMENT:
      case ContractProperties.MIN_SUBSCRIPTION_MONTHS:
      case ContractProperties.BASE_FEE:
      case ContractProperties.IS_FEE:
      case ContractProperties.AC_FEE:
      case ContractProperties.CHALLENGE_FEE:
      case ContractProperties.AI_FRIEND_FEE:
      case ContractProperties.UPSELL_MONTHLY_PRICE:
        newContractValues = {
          [name]: Number(value),
        };
        break;
      default:
        newContractValues = {
          [name]: value,
        };
        break;
    }

    /*
      In custom pricing, editing a price amount means the contract no longer matches the package's preset Stripe
      price, so we drop the preset priceId/monthlyPriceId and let the backend mint a dynamic price from the entered
      amounts. Leaving the amounts untouched keeps the priceId that was set on package selection, so an unmodified
      custom contract still reuses the package's price.
    */
    const isPriceAmountField = name === ContractProperties.INITIAL_PAYMENT
      || name === ContractProperties.MONTHLY_PAYMENT;
    if (isPriceAmountField && selectedPriceType === ContractPriceType.CUSTOM) {
      newContractValues[ContractProperties.PRICE_ID] = '';
      newContractValues[ContractProperties.MONTHLY_PRICE_ID] = '';
    }

    setContract((currentContract) => ({
      ...currentContract,
      ...newContractValues,
    }));
  }, [
    contract,
    isChallengeContract,
    selectedPriceType,
  ]);

  /**
   * Apply a package's pricing to the contract being written.
   *
   * @param {object} packageItem The package picked.
   * @param {object} [packageProduct] The product it belongs to, when the pick changes product too.
   *   Passed because selecting a card sets the product and the package in one go, and the product
   *   state has not updated yet — reading the selected product here would price the package with the
   *   outgoing product's rules, which coming off a challenge means pinning the monthly amount to the
   *   initial one and dropping the package's own monthly price.
   */
  const onPackageSelected = useCallback((packageItem, packageProduct) => {
    const isChallengePackage = packageProduct ? !!packageProduct.isChallengeProduct : isChallengeContract;
    const {
      initialPaymentInCents: initialPayment,
      initialTerm,
      minSubscriptionMonths,
      monthlyPaymentInCents: monthlyPayment,
      priceId,
      monthlyPriceId,
      currency,
      id: packageId,
      name: packageName,
    } = packageItem;

    if (typeof initialPayment !== 'number' || typeof monthlyPayment !== 'number') {
      const errorMessage = format(texts.packageError, { packageName: packageItem.name });
      showToast(errorMessage, { error: true });

      Sentry.captureException(new Error(errorMessage), {
        extra: {
          package: JSON.stringify(packageItem, null, 2),
          coachId: coachDoc.id,
        },
      });
      return;
    }

    const contractFees = getContractFees();
    setContract(({
      [ContractProperties.SELLER_ID]: currentSellerId,
      [ContractProperties.NAME]: currentName,
      [ContractProperties.START_DATE]: currentStartDate,
      [ContractProperties.CANCEL_AT_PERIOD_END]: currentCancelAtPeriodEnd,
    }) => ({
      ...DefaultContract,
      ...contractFees,
      [ContractProperties.INITIAL_PAYMENT]: formatAmoutInCents(initialPayment, MONEY_DECIMALS),
      priceId,
      initialTerm,
      // For challenge contracts, minSubscriptionMonths should equal initialTerm
      minSubscriptionMonths: isChallengePackage ? initialTerm : minSubscriptionMonths,
      [ContractProperties.MONTHLY_PAYMENT]: formatAmoutInCents(
        isChallengePackage ? initialPayment : monthlyPayment,
        MONEY_DECIMALS,
      ),
      /*
        Pinning the amount above means the package's preset monthly price no longer describes it, so it
        is dropped for the same reason onContractFieldChange drops it when a coach edits an amount. The
        one-time challenge path builds its price from cents and never reads this, but a stored contract
        that contradicts itself is a trap for anything that later does.
      */
      monthlyPriceId: isChallengePackage ? '' : monthlyPriceId,
      currency,
      coachConditions: coachDoc.termsAndConditionsText,
      packageId,
      packageName,
      [ContractProperties.NAME]: currentName || DefaultContract[ContractProperties.NAME],
      ...(currentSellerId ? { [ContractProperties.SELLER_ID]: currentSellerId } : {}),
      ...(currentStartDate
        ? { [ContractProperties.START_DATE]: currentStartDate }
        : DefaultContract[ContractProperties.START_DATE]
      ),
      ...(isChallengePackage && { [ContractProperties.CANCEL_AT_PERIOD_END]: true }),
      // For preview contracts, we set the challenge ID to 'preview' as a placeholder.
      ...(isChallengePackage && { [ContractProperties.CHALLENGE_ID]: CHALLENGE_ID_PREVIEW }),
      ...(!isChallengePackage && typeof currentCancelAtPeriodEnd !== 'undefined' && {
        [ContractProperties.CANCEL_AT_PERIOD_END]: currentCancelAtPeriodEnd,
      }),
    }));
  }, [
    coachDoc,
    showToast,
    getContractFees,
    isChallengeContract,
  ]);

  const onProductChange = useCallback((product) => {
    setSelectedProduct(product);
    setContract((prevContract) => {
      const {
        [ContractProperties.SELLER_ID]: currentSellerId,
        [ContractProperties.NAME]: currentName,
        [ContractProperties.START_DATE]: currentStartDate,
      } = prevContract;

      const isRegularProduct = !product?.isChallengeProduct && !product?.isAiFriendEnabled;
      let contractFees;
      if (!isRegularProduct && contractDoc) {
        // Updating an existing contract: derive fees from the previous contract
        const feeProperty = product?.isChallengeProduct
          ? ContractProperties.CHALLENGE_FEE
          : ContractProperties.AI_FRIEND_FEE;
        contractFees = getSingleFeeFromExisting(prevContract, feeProperty);
      } else if (isRegularProduct && contractDoc && prevContract[ContractProperties.AI_FRIEND_FEE] !== undefined) {
        // Switching back to regular on an existing contract: restore original fees
        const originalFeesFromContractDoc = getOriginalFeesFromContractDoc(contractDoc);
        contractFees = Object.keys(originalFeesFromContractDoc).length
          ? originalFeesFromContractDoc
          : getContractFees(product);
      } else {
        // Creating a new contract: always use fresh defaults
        contractFees = getContractFees(product);
      }

      const isChallenge = !!product?.isChallengeProduct;
      return {
        ...DefaultContract,
        ...contractFees,
        [ContractProperties.PACKAGE_ID]: '',
        [ContractProperties.PACKAGE_NAME]: '',
        [ContractProperties.NAME]: currentName || DefaultContract[ContractProperties.NAME],
        ...(currentSellerId ? { [ContractProperties.SELLER_ID]: currentSellerId } : {}),
        ...(currentStartDate
          ? { [ContractProperties.START_DATE]: currentStartDate }
          : DefaultContract[ContractProperties.START_DATE]
        ),
        ...(isChallenge && {
          [ContractProperties.CHALLENGE_ID]: CHALLENGE_ID_PREVIEW,
          [ContractProperties.CANCEL_AT_PERIOD_END]: true,
          [ContractProperties.MONTHLY_PAYMENT]: CHALLENGE_CONTRACT_MONTHLY_PAYMENT_DEFAULT,
          [ContractProperties.MIN_SUBSCRIPTION_MONTHS]: DefaultContract[ContractProperties.INITIAL_TERM],
        }),
      };
    });
  }, [
    setSelectedProduct,
    contractDoc,
    getContractFees,
  ]);

  /**
   * Calculates secondary contract data based on the primary contract and lead config
   * @param {Object} primaryContract - The primary contract data
   * @param {Object} selectedProduct - The selected product
   * @param {Object} coachDoc - The coach document
   * @param {string} sellerId - The seller ID
   * @returns {Object} Secondary contract data ready for document creation/update
   */
  const calculateSecondaryContractData = useCallback((
    primaryContract,
    product,
    coach,
    sellerId,
  ) => {
    const { initialDiscountPercentage, initialSubscriptionMonths } = leadConfigDoc;
    const discountMultiplier = (100 - initialDiscountPercentage) / 100;

    // Create modified contract data for secondary contract
    const secondaryContractData = {
      ...primaryContract,
      [ContractProperties.INITIAL_TERM]: initialSubscriptionMonths,
      // minimum subscription months should be at least the initial term
      [ContractProperties.MIN_SUBSCRIPTION_MONTHS]:
        primaryContract[ContractProperties.MIN_SUBSCRIPTION_MONTHS] > initialSubscriptionMonths
          ? primaryContract[ContractProperties.MIN_SUBSCRIPTION_MONTHS]
          : initialSubscriptionMonths,
      /*
        Both terms above come from leadConfigDoc and are counted in months, so
        the unit is pinned rather than inherited from the spread. A secondary
        contract is always a monthly subscription; letting a week unit ride
        along from a challenge primary would reinterpret those month counts as
        weeks in the copy and the terms while Stripe still billed months.
      */
      [ContractProperties.DURATION_UNIT]: DURATION_UNIT.MONTH,
      // initial payment should be the discounted initial payment calculated for the new initial term
      [ContractProperties.INITIAL_PAYMENT]: (
        parseFloat(primaryContract[ContractProperties.INITIAL_PAYMENT])
        * discountMultiplier * initialSubscriptionMonths
      ).toFixed(2),
    };

    // Generate secondary contract document content using the same function
    const secondaryContractDocContent = getContractDocContent({
      lead,
      user,
      contract: secondaryContractData,
      product,
      coachDoc: coach,
      sellerId,
      isAmendment: false,
    });

    return {
      ...secondaryContractDocContent,
      [ContractProperties.TYPE]: ContractType.USER,
      price: {
        ...secondaryContractDocContent.price,
        initialDiscountPercentage, // Save discount rate as context data
        priceId: '', // This is a custom pricing, so we need to avoid using the priceId from the product.
      },
    };
  }, [lead, user, leadConfigDoc]);

  const getOrCreateChallengeClientTag = useCallback(async (challengeName) => {
    const existingCoachClientTags = await Tag.getClientCustomTagsforCoach(coachDoc.id);
    const existingTag = existingCoachClientTags.docs.find((tagDoc) => (
      tagDoc.tag?.trim().toLowerCase() === challengeName?.trim().toLowerCase()
    ));

    if (existingTag) {
      return existingTag.tag;
    }

    await Tag.addDoc({
      tag: challengeName.trim(),
      type: TagType.CLIENT,
      coach: coachDoc.id,
      isS2tag: false,
    });
    return challengeName.trim();
  }, [coachDoc]);

  /**
   * Generates a contract based on the existing context data.
   * if the contract is already saved, it will update the existing contract with updated data.
   * If the contract is not saved, it will create a new one.
   * @returns {Object} An object indicating the success of the operation.
   */
  const generateContract = useCallback(async () => {
    // This will hold the new contract content, if we need to create one.
    let newContract;

    // This will hold the secondary contract content, if we need to create one.
    let secondaryContract;

    setIsSaving(true);

    const {
      isEnrolledInISProgram,
    } = coachDoc;

    /*
      First, we need to calculate the seller ID based on different conditions.
      If this is a one-time contract, we use the coach ID.
      If this is a reonboarding contract, we have to check whether it is being created
      within the first month of the subscription, or after.
      In the case of IS agents and coaches, their ids are always saved on the contract.
      For ACs and support agents, though, we have to check for two conditions:
        1. If the contract is created within the first month, we use the values extracted from that contract.
        2. If the contract is created after the first month, we use the REONBOARDING_INSIDE_SALES_ID.
    */
    // Default to the logged in user, which covers the case of coaches creating contracts.
    let sellerId = loggedInUserDoc.id;

    if (contractType === ContractType.ONE_TIME) {
      sellerId = coachDoc.id;
    } else if (isEnrolledInISProgram && isLoggedInUserInsideSales) {
      sellerId = loggedInUserDoc.id;
    } else if (isReonboardingByACOrSupport) {
      if (isWithinFirstMonth) {
        sellerId = latestContract?.sellerId;
      } else {
        sellerId = REONBOARDING_INSIDE_SALES_ID;
      }
    }

    const contractDocContent = getContractDocContent({
      lead,
      user,
      contract,
      product: selectedProduct,
      coachDoc,
      sellerId,
      isAmendment,
      originalContract: supersededContractId || fallbackContractId,
    });

    try {
      // If there's a contract document loaded, then this is an update.
      if (contractDoc) {
        /*
        Verify that the lead ID is the same as the one in the contract.
        Or if it is a reusable contract or a one-time contract which doesn't need a leadId.
        If it's a reonboarding contract, verify that the user ID is the
        same as the one in the contract.
        */
        if ((contractType === ContractType.BASE
          || contractType === ContractType.ONE_TIME
          || contractDoc.leadId === lead.id)
          || (isReonboardingContract && contractDoc.userId === user.id)) {
          await contractDoc.update({
            ...contractDocContent,
            updatedAt: new Date(),
            updatedBy: loggedInUserDoc.id,
            [ContractProperties.TYPE]: contractType,
            ...(isChallengeContract && acceptUntil && { acceptUntil: acceptUntil.toDate() }),
          });

          // Update challenge config if this is a challenge contract
          if (isChallengeContract && contractDoc.challengeId) {
            try {
              const challengeConfig = await ChallengeConfig.getChallengeConfigById(
                contractDoc.challengeId,
              );
              if (challengeConfig) {
                await challengeConfig.updateFields({
                  mealPlanId: selectedMealPlanId,
                  programId: selectedProgramId,
                });
              }
            } catch (error) {
              Sentry.captureException(new Error('Error updating challenge config'), {
                extra: {
                  challengeId: contractDoc.challengeId,
                  selectedProgramId,
                  selectedMealPlanId,
                  error,
                },
              });
            }
          }

          logCoachingActivity(CoachingActivity.CONTRACT_UPDATED, {
            contractId: contractDoc.id,
            productId: selectedProduct?.id,
          });

          // Handle secondary contract updates/deletions
          if (secondaryContractDoc) {
            try {
              /*
                Secondary contract should be deleted if:
                1. The primary contract is a PIF contract (initialTerm > 1), or
                2. The primary contract is a discounted monthly contract (initialPayment != monthlyPayment), or
                3. The primary contract's initial payment has become 0
              */
              const shouldDeleteSecondaryContract = (
                contract[ContractProperties.INITIAL_TERM] > 1
                || Number(contract[ContractProperties.INITIAL_PAYMENT])
                !== Number(contract[ContractProperties.MONTHLY_PAYMENT])
                || Number(contract[ContractProperties.INITIAL_PAYMENT]) === 0
              );

              if (shouldDeleteSecondaryContract) {
                // Delete secondary contract and remove the reference from the primary contract
                await secondaryContractDoc.delete();
                await contractDoc.update({
                  secondaryContractId: firebase.firestore.FieldValue.delete(),
                });
              } else {
                // Update secondary contract with recalculated data
                const secondaryContractData = calculateSecondaryContractData(
                  contract,
                  selectedProduct,
                  coachDoc,
                  sellerId,
                );

                await secondaryContractDoc.update({
                  ...secondaryContractData,
                  updatedAt: new Date(),
                });
              }

              // Check if document was deleted and clear state if so
              if (!secondaryContractDoc.exists) {
                setSecondaryContractDoc(null);
              }
            } catch (error) {
              Sentry.captureException(error, {
                extra: {
                  primaryContractId: contractDoc.id,
                  secondaryContractId: contractDoc.secondaryContractId,
                },
              });
            }
          }
        } else {
          const errorMessage = isReonboardingContract ? texts.contractUserIdError : texts.contractLeadIdError;
          Sentry.captureException(
            new Error(errorMessage),
            {
              extra: {
                contract: contractDoc.id,
                contractLeadId: contractDoc.leadId,
                contractUserId: contractDoc.userId,
                leadId: lead.id,
                userId: user.id,
              },
            },
          );
          showToast(errorMessage, { error: true });
          setIsSaving(false);
          return { isSuccessful: false };
        }
      } else {
        // If this is a challenge contract, create the challenge config first
        let challengeConfigId;
        if (isChallengeContract) {
          try {
            const challengeTag = await getOrCreateChallengeClientTag(contractDocContent.name);
            const challengeConfigDoc = await ChallengeConfig.addDoc({
              mealPlanId: selectedMealPlanId,
              programId: selectedProgramId,
              tag: challengeTag,
            });
            challengeConfigId = challengeConfigDoc.id;
          } catch (error) {
            Sentry.captureException(new Error('Error creating challenge config'), {
              extra: {
                selectedProgramId,
                selectedMealPlanId,
                error,
              },
            });
            if (isComponentMountedRef.current) {
              showToast(texts.challengeConfigError, { error: true });
              setIsSaving(false);
            }
            return { isSuccessful: false };
          }
        }

        const newDocContent = {
          ...contractDocContent,
          createdAt: new Date(),
          ...(contractType === ContractType.BASE && { createdBy: loggedInUserDoc.id }),
          [ContractProperties.TYPE]: contractType,
          challengeId: challengeConfigId || '',
          ...(acceptUntil && { acceptUntil: acceptUntil.toDate() }),
        };

        newContract = await UserContract.addDoc(newDocContent);

        logCoachingActivity(CoachingActivity.CONTRACT_CREATED, {
          contractId: newContract.id,
          productId: selectedProduct?.id,
        });

        // If there is a lead, we update the lead with the contract creation date.
        if (lead?.exists) {
          await lead.updateFields({
            contractCreatedAt: moment().toDate(),
          });
        }

        /**
         * We only create a secondary contract if the following criteria are met:
         * 1. It's a new contract for a lead
         * 2. The lead config is enabled
         * 3. The coach is enrolled in the IS program and the logged in user is an IS agent
         * 4. The contract is a monthly contract
         * 5. The initial payment is greater than 0
         * 6. The initial payment is not a discounted rate (i.e. it's the same as the monthly payment)
         * 7. The client is randomly eligible for the discount offer
         */
        const shouldCreateSecondaryContract = (
          !!newContract && !!lead.id
          && !!leadConfigDoc && leadConfigDoc.isDiscountOffersEnabled
          && isEnrolledInISProgram && isLoggedInUserInsideSales
          && contract[ContractProperties.INITIAL_TERM] === 1
          /*
            The equality below is a proxy for "an undiscounted monthly deal". Pinning the monthly
            payment to the initial payment makes every challenge satisfy it, and a challenge has no
            monthly rate to discount — so it is excluded explicitly rather than by arithmetic.
          */
          && !isChallengeContract
          && Number(contract[ContractProperties.INITIAL_PAYMENT]) > 0
          && Number(contract[ContractProperties.INITIAL_PAYMENT])
          === Number(contract[ContractProperties.MONTHLY_PAYMENT])
          && Math.random() < leadConfigDoc.applicableUserRatio
        );

        if (shouldCreateSecondaryContract) {
          try {
            // Calculate secondary contract data using the extracted function
            const secondaryContractData = calculateSecondaryContractData(
              contract,
              selectedProduct,
              coachDoc,
              sellerId,
            );

            // Add secondary contract specific fields
            const finalSecondaryContractData = {
              ...secondaryContractData,
              createdAt: new Date(),
              // Link to primary contract
              primaryContractId: newContract.id,
            };

            secondaryContract = await UserContract.addDoc(finalSecondaryContractData);

            // Update primary contract with secondary contract ID
            await newContract.update({
              secondaryContractId: secondaryContract.id,
            });
          } catch (error) {
            Sentry.captureException(error, {
              extra: {
                primaryContractId: newContract.id,
                leadConfigDoc,
              },
            });
          }
        }
      }

      /*
        A contract supersedes any challenge the lead was holding: the rep moved them onto this
        offer. Left behind, the stale challenge resurfaces if this contract is ever cancelled.
      */
      if (lead?.id && lead.selectedChallengeContractId) {
        try {
          await lead.setSelectedChallengeContract('');
        } catch (error) {
          Sentry.captureException(error, { extra: { leadId: lead.id } });
        }
      }

      if (isComponentMountedRef.current) {
        // If a new contract has been generated, then we use it from now on.
        if (newContract) {
          setContractDoc(newContract);
        }
        // Set the secondary contract doc if it was created.
        if (secondaryContract) {
          setSecondaryContractDoc(secondaryContract);
        }
        setIsSaving(false);
        setIsPreviewTerms(false);
        loadContractFromDoc(newContract || contractDoc);
      }
      return { isSuccessful: true };
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(texts.contractError, { error: true });
        Sentry.captureException(error, {
          extra: {
            contract: JSON.stringify(contractDocContent, null, 2),
          },
        });
        setIsSaving(false);
      }
    }
    return { isSuccessful: false };
  }, [
    coachDoc,
    contract,
    contractDoc,
    selectedProduct,
    loggedInUserDoc,
    isComponentMountedRef,
    showToast,
    lead,
    contractType,
    user,
    isReonboardingContract,
    loadContractFromDoc,
    latestContract.sellerId,
    isWithinFirstMonth,
    isLoggedInUserInsideSales,
    isReonboardingByACOrSupport,
    isAmendment,
    supersededContractId,
    fallbackContractId,
    leadConfigDoc,
    calculateSecondaryContractData,
    secondaryContractDoc,
    acceptUntil,
    isChallengeContract,
    selectedProgramId,
    selectedMealPlanId,
    logCoachingActivity,
    getOrCreateChallengeClientTag,
  ]);

  const editContract = useCallback(() => {
    setContractState(ContractState.CONFIGURATION);
  }, []);

  const viewTerms = useCallback(() => {
    /*
      If we are viewing the terms, so we can set the current contract.
    */
    if (!isReonboardingContract) {
      setContract({ ...contract });
    }

    setIsPreviewTerms(true);
    setContractState(ContractState.DETAILS);
  }, [
    isReonboardingContract,
    contract,
  ]);

  const cancelEditContract = useCallback(() => {
    /*
      When navigating back to view the terms, reset the contract to its previous state.
    */
    if (contractDoc) {
      loadContractFromDoc(contractDoc);
    }
    setIsPreviewTerms(false);
    setContractState(ContractState.DETAILS);
  }, [
    contractDoc,
    loadContractFromDoc,
  ]);

  /*
    Generate the terms when the contract changes. We have to use a memo to avoid generating the terms on every
    render. Even though this is not a heavy operation, the context would change on every render, and that would
    cause all children to re-render unnecessary.
  */
  const contractTerms = useMemo(() => generateTerms(contract, isAmendment), [contract, isAmendment]);

  /*
    We should not make changes to a contract if;
    1. A primary/single contract exists and is not in the PENDING status (meaning it was already used).
    2. The selected contract is a secondary contract.
    2. A secondary contract exists and is not in the PENDING status (meaning it was already used).
  */
  const isReadOnly = (!!contractDoc && (
    contractDoc.status !== ContractStatus.PENDING
    || (contractDoc.type === ContractType.BASE && contractDoc.usageCount > 0)
  ))
    || (!!contract && !!contract.primaryContractId)
    || (!!secondaryContractDoc && secondaryContractDoc.status !== ContractStatus.PENDING);

  const context = useMemo(() => ({
    coachDoc,
    externalCoachDoc,
    products,
    contract,
    contractDoc,
    secondaryContractDoc,
    loadContractFromDoc,
    contractState,
    selectedProduct,
    isLoading,
    isLoadingContract,
    isSaving,
    isValidContract,
    isReadOnly,
    contractTerms,
    contractType,
    lead,
    user,
    isReonboardingContract,
    coachId,
    setCoachId,
    loadContractByLead,
    loadReonboardingContractByUser,
    loadReusableContractFromDoc,
    onContractFieldChange,
    onPackageSelected,
    onProductChange,
    resetContract,
    generateContract,
    editContract,
    viewTerms,
    setContractState,
    setContractType,
    isAmendment,
    setIsAmendment,
    setSupersededContractId,
    blockedProductIds,
    selectedPriceType,
    setSelectedPriceType,
    cancelEditContract,
    isPreviewTerms,
    isChallengeContract,
    selectedProgramId,
    setSelectedProgramId,
    setSelectedProgramWorkoutDays,
    selectedMealPlanId,
    setSelectedMealPlanId,
    acceptUntil,
    setAcceptUntil,
    categoryPackageEntries,
    categoryChallengeContract,
    isCategoryChallengeSelected,
    challengeLinkContractId,
    canSaveChallengeForLead,
    isSelectedChallengeSaved,
    isChoosingOffering,
    hasCardSelection,
    clearCardSelection,
    markCardPicked,
    onboardingLinkRecipient,
    onboardingLink,
    selectCategoryChallenge,
    saveChallengeForLead,
    isSavingChallenge,
    showCategoryCards,
  }), [
    coachDoc,
    externalCoachDoc,
    products,
    contract,
    contractDoc,
    secondaryContractDoc,
    loadContractFromDoc,
    contractState,
    selectedProduct,
    isLoading,
    isLoadingContract,
    isSaving,
    isValidContract,
    isReadOnly,
    contractTerms,
    contractType,
    lead,
    user,
    isReonboardingContract,
    coachId,
    setCoachId,
    loadContractByLead,
    loadReonboardingContractByUser,
    loadReusableContractFromDoc,
    onContractFieldChange,
    onPackageSelected,
    onProductChange,
    resetContract,
    generateContract,
    editContract,
    viewTerms,
    setContractState,
    setContractType,
    isAmendment,
    setIsAmendment,
    setSupersededContractId,
    blockedProductIds,
    selectedPriceType,
    setSelectedPriceType,
    cancelEditContract,
    isPreviewTerms,
    isChallengeContract,
    selectedProgramId,
    setSelectedProgramId,
    setSelectedProgramWorkoutDays,
    selectedMealPlanId,
    setSelectedMealPlanId,
    acceptUntil,
    setAcceptUntil,
    categoryPackageEntries,
    categoryChallengeContract,
    isCategoryChallengeSelected,
    challengeLinkContractId,
    canSaveChallengeForLead,
    isSelectedChallengeSaved,
    isChoosingOffering,
    hasCardSelection,
    clearCardSelection,
    markCardPicked,
    onboardingLinkRecipient,
    onboardingLink,
    selectCategoryChallenge,
    saveChallengeForLead,
    isSavingChallenge,
    showCategoryCards,
  ]);

  return (
    <ContractModalContext.Provider value={context}>
      {children}
    </ContractModalContext.Provider>
  );
};

ContractModalContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ContractModalContextProvider);
